import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Button, 
  Avatar, 
  Divider, 
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Chip,
  useTheme,
  alpha,
  Stack,
  Tooltip
} from '@mui/material';
import { 
  Google, 
  GitHub, 
  Person, 
  Email, 
  Link as LinkIcon,
  LinkOff,
  ExitToApp,
  DeleteForever,
  Warning,
  Public,
  ContentCopy,
  AccountCircle,
  Security,
  Settings,
  Share,
  Verified,
  Edit
} from '@mui/icons-material';
import { 
  signOut, 
  linkWithPopup, 
  unlink, 
  GoogleAuthProvider, 
  GithubAuthProvider,
  deleteUser,
  reauthenticateWithPopup
} from 'firebase/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, githubProvider } from '../firebase';
import { useUserProfile } from '../hooks/useUserProfile';
import UsernameEditor from './UsernameEditor';

const ProfilePage = () => {
  const [user, loading] = useAuthState(auth);
  const { userProfile, loading: profileLoading, updateProfile } = useUserProfile();
  const [isLinking, setIsLinking] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    if (!loading && !profileLoading && !user) {
      navigate('/auth');
    }
  }, [user, loading, profileLoading, navigate]);

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/auth');
    } catch (error) {
      console.error('Sign out error:', error);
      showMessage('Error signing out. Please try again.', 'error');
    }
  };

  const linkGoogleAccount = async () => {
    if (!user) return;
    
    setIsLinking(true);
    const provider = new GoogleAuthProvider();
    
    try {
      await linkWithPopup(user, provider);
      showMessage('Google account linked successfully!', 'success');
    } catch (error) {
      console.error('Google linking error:', error);
      
      if (error.code === 'auth/provider-already-linked') {
        showMessage('Google account is already linked to this account.', 'info');
      } else if (error.code === 'auth/credential-already-in-use') {
        showMessage('This Google account is already linked to another account.', 'error');
      } else if (error.code === 'auth/email-already-in-use') {
        showMessage('This email is already registered with a different account. Please use a different Google account.', 'error');
      } else if (error.code === 'auth/popup-closed-by-user') {
        showMessage('Link cancelled by user.', 'info');
      } else {
        showMessage('Failed to link Google account. Please try again.', 'error');
      }
    } finally {
      setIsLinking(false);
    }
  };

  const linkGithubAccount = async () => {
    if (!user) return;
    
    setIsLinking(true);
    
    try {
      await linkWithPopup(user, githubProvider);
      showMessage('GitHub account linked successfully!', 'success');
    } catch (error) {
      console.error('GitHub linking error:', error);
      
      if (error.code === 'auth/provider-already-linked') {
        showMessage('GitHub account is already linked to this account.', 'info');
      } else if (error.code === 'auth/credential-already-in-use') {
        showMessage('This GitHub account is already linked to another account.', 'error');
      } else if (error.code === 'auth/email-already-in-use') {
        showMessage('This email is already registered with a different account. Please use a different GitHub account.', 'error');
      } else if (error.code === 'auth/popup-closed-by-user') {
        showMessage('Link cancelled by user.', 'info');
      } else {
        showMessage('Failed to link GitHub account. Please try again.', 'error');
      }
    } finally {
      setIsLinking(false);
    }
  };

  const unlinkProvider = async (providerId) => {
    if (!user) return;
    
    setIsLinking(true);
    
    try {
      await unlink(user, providerId);
      const providerName = providerId === 'google.com' ? 'Google' : 'GitHub';
      showMessage(`${providerName} account unlinked successfully!`, 'success');
    } catch (error) {
      console.error('Unlink error:', error);
      showMessage('Failed to unlink account. Please try again.', 'error');
    } finally {
      setIsLinking(false);
    }
  };

  const getLinkedProviders = () => {
    if (!user || !user.providerData) return [];
    return user.providerData.map(provider => provider.providerId);
  };

  const isProviderLinked = (providerId) => {
    return getLinkedProviders().includes(providerId);
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    
    setIsDeleting(true);
    
    try {
      // Try to delete the user directly first
      await deleteUser(user);
      showMessage('Account deleted successfully!', 'success');
      
      // Navigate to auth page after a brief delay
      setTimeout(() => {
        navigate('/auth');
      }, 2000);
      
    } catch (error) {
      console.error('Delete account error:', error);
      
      if (error.code === 'auth/requires-recent-login') {
        // User needs to reauthenticate
        try {
          // Get the most recent provider used
          const recentProvider = user.providerData[0];
          let provider;
          
          if (recentProvider.providerId === 'google.com') {
            provider = new GoogleAuthProvider();
          } else if (recentProvider.providerId === 'github.com') {
            provider = githubProvider;
          } else {
            throw new Error('Unsupported provider for reauthentication');
          }
          
          // Reauthenticate and then delete
          await reauthenticateWithPopup(user, provider);
          await deleteUser(user);
          
          showMessage('Account deleted successfully!', 'success');
          setTimeout(() => {
            navigate('/auth');
          }, 2000);
          
        } catch (reauthError) {
          console.error('Reauthentication error:', reauthError);
          
          if (reauthError.code === 'auth/popup-closed-by-user') {
            showMessage('Account deletion cancelled. Please try again and complete the authentication.', 'info');
          } else {
            showMessage('Failed to delete account. Please sign out and sign in again, then try deleting your account.', 'error');
          }
        }
      } else {
        showMessage('Failed to delete account. Please try again.', 'error');
      }
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setConfirmationText('');
    }
  };

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setConfirmationText('');
  };

  const isDeleteConfirmed = () => {
    return confirmationText.toLowerCase() === 'delete my account';
  };

  const handleUsernameUpdate = (newUsername) => {
    updateProfile({ username: newUsername });
    showMessage('Username updated successfully!', 'success');
  };

  const handleViewPublicProfile = () => {
    if (userProfile?.username) {
      window.open(`/u/${userProfile.username}`, '_blank');
    }
  };

  const handleCopyProfileLink = async () => {
    if (userProfile?.username) {
      try {
        const profileUrl = `${window.location.origin}/u/${userProfile.username}`;
        await navigator.clipboard.writeText(profileUrl);
        showMessage('Profile link copied to clipboard!', 'success');
      } catch (error) {
        showMessage('Failed to copy link', 'error');
      }
    }
  };

  if (loading || profileLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            minHeight: '50vh',
            justifyContent: 'center'
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading your profile...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 0, sm: 2 } }}>
      <Paper elevation={3} sx={{ p: { xs: 1, sm: 2, md: 4 } }}>
        {/* Profile Header */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: { xs: 2, sm: 0 } }}>
          <Avatar 
            src={user.photoURL} 
            sx={{ width: { xs: 64, sm: 80 }, height: { xs: 64, sm: 80 }, mr: { xs: 0, sm: 3 }, mb: { xs: 2, sm: 0 } }}
            imgProps={{
              crossOrigin: 'anonymous',
              referrerPolicy: 'no-referrer'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          >
            <Person />
          </Avatar>
          <Box sx={{ width: '100%' }}>
            <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              {userProfile?.displayName || user?.displayName || 'User Profile'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              <Email sx={{ mr: 1 }} />
              {user?.email}
            </Typography>
            {userProfile?.username && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: 'monospace' }}>
                @{userProfile.username}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Messages */}
        {message && (
          <Alert severity={messageType} sx={{ mb: 3 }}>
            {message}
          </Alert>
        )}

        {/* Username Editor */}
        {userProfile?.username && (
          <>
            <UsernameEditor
              currentUsername={userProfile.username}
              userId={user?.uid}
              onUsernameUpdate={handleUsernameUpdate}
            />
            
            {/* Public Profile Actions */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Public Profile
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Share your profile and showcase your forms
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<Public />}
                  onClick={handleViewPublicProfile}
                  size="small"
                >
                  View Public Profile
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopy />}
                  onClick={handleCopyProfileLink}
                  size="small"
                >
                  Copy Link
                </Button>
              </Box>
            </Box>
          </>
        )}

        {/* Linked Accounts Section */}
        <Typography variant="h5" gutterBottom sx={{ mb: 2, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
          Linked Accounts
        </Typography>
        
        <List>
          {/* Google Account */}
          <ListItem sx={{ alignItems: 'center' }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Google sx={{ color: '#4285F4' }} />
            </ListItemIcon>
            {isProviderLinked('google.com') ? (
              <IconButton 
                onClick={() => unlinkProvider('google.com')}
                disabled={isLinking || getLinkedProviders().length === 1}
                color="error"
                sx={{ ml: 1 }}
                aria-label="Unlink Google"
              >
                <LinkOff />
              </IconButton>
            ) : (
              <IconButton
                onClick={linkGoogleAccount}
                disabled={isLinking}
                sx={{ color: '#4285F4', ml: 1 }}
                aria-label="Link Google"
              >
                <LinkIcon />
              </IconButton>
            )}
            <ListItemText 
              primary="Google Account"
              secondary={isProviderLinked('google.com') ? 'Connected' : 'Not connected'}
              sx={{ ml: 2 }}
            />
          </ListItem>

          {/* GitHub Account */}
          <ListItem sx={{ alignItems: 'center' }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <GitHub sx={{ color: '#24292e' }} />
            </ListItemIcon>
            {isProviderLinked('github.com') ? (
              <IconButton 
                onClick={() => unlinkProvider('github.com')}
                disabled={isLinking || getLinkedProviders().length === 1}
                color="error"
                sx={{ ml: 1 }}
                aria-label="Unlink GitHub"
              >
                <LinkOff />
              </IconButton>
            ) : (
              <IconButton
                onClick={linkGithubAccount}
                disabled={isLinking}
                sx={{ color: '#24292e', ml: 1 }}
                aria-label="Link GitHub"
              >
                <LinkIcon />
              </IconButton>
            )}
            <ListItemText 
              primary="GitHub Account"
              secondary={isProviderLinked('github.com') ? 'Connected' : 'Not connected'}
              sx={{ ml: 2 }}
            />
          </ListItem>
        </List>

        {getLinkedProviders().length === 1 && (
          <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
            You must have at least one linked account to maintain access to your profile.
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Account Actions */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: { sm: 'space-between' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Button
            fullWidth={true}
            variant="outlined"
            startIcon={<DeleteForever />}
            onClick={openDeleteDialog}
            color="error"
            disabled={isDeleting}
            sx={{ mb: { xs: 1, sm: 0 } }}
          >
            Delete Account
          </Button>
          
          <Button
            fullWidth={true}
            variant="outlined"
            startIcon={<ExitToApp />}
            onClick={handleSignOut}
            color="primary"
          >
            Sign Out
          </Button>
        </Box>
      </Paper>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="error" />
          Delete Account
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
          <DialogContentText sx={{ mb: 3 }}>
            Deleting your account will permanently remove:
          </DialogContentText>
          <Box component="ul" sx={{ pl: 2, mb: 3 }}>
            <li><Typography variant="body2">Your profile information</Typography></li>
            <li><Typography variant="body2">All your created forms</Typography></li>
            <li><Typography variant="body2">All form submissions</Typography></li>
            <li><Typography variant="body2">All associated data</Typography></li>
          </Box>
          <DialogContentText sx={{ mb: 2 }}>
            To confirm deletion, please type: <strong>delete my account</strong>
          </DialogContentText>
          <TextField
            fullWidth
            label="Confirmation"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder="delete my account"
            variant="outlined"
            error={confirmationText.length > 0 && !isDeleteConfirmed()}
            helperText={confirmationText.length > 0 && !isDeleteConfirmed() ? 'Please type exactly: delete my account' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={!isDeleteConfirmed() || isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteForever />}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfilePage;
